import {
  EnvironmentInjector,
  Injectable,
  Signal,
  WritableSignal,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

export interface CacheOptions {
  ttlMs?: number;
  invalidateOn?: Signal<unknown>;
}

export interface CacheRef<T> {
  readonly data: Signal<T[]>;
  readonly isLoading: Signal<boolean>;
  readonly hasError: Signal<boolean>;
  load(): void;
  invalidate(): void;
}

interface CacheEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _data: WritableSignal<any[]>;
  _isLoading: WritableSignal<boolean>;
  _hasError: WritableSignal<boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obs$: Observable<any[]> | undefined;
  loadedAt: number | undefined;
  ttlMs: number | undefined;
}

@Injectable({ providedIn: 'root' })
export class DropdownCacheService {
  private readonly injector = inject(EnvironmentInjector);
  private readonly entries = new Map<string, CacheEntry>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly loaders = new Map<string, () => Observable<any[]>>();

  /**
   * Registers a cached data source identified by key.
   * Subsequent calls with the same key return a ref to the existing cache —
   * the loaderFn and options from the first call always win.
   */
  register<T>(
    key: string,
    loaderFn: () => Observable<T[]>,
    options?: CacheOptions,
  ): CacheRef<T> {
    if (!this.entries.has(key)) {
      this.entries.set(key, {
        _data: signal<T[]>([]),
        _isLoading: signal(false),
        _hasError: signal(false),
        obs$: undefined,
        loadedAt: undefined,
        ttlMs: options?.ttlMs,
      });
      this.loaders.set(key, loaderFn);

      if (options?.invalidateOn) {
        let firstRun = true;
        effect(
          () => {
            options.invalidateOn!();
            if (firstRun) { firstRun = false; return; }
            untracked(() => this.invalidate(key));
          },
          { injector: this.injector },
        );
      }
    }

    const entry = this.entries.get(key)!;

    return {
      data: entry._data.asReadonly() as Signal<T[]>,
      isLoading: entry._isLoading.asReadonly(),
      hasError: entry._hasError.asReadonly(),
      load: () => this._load(key),
      invalidate: () => this.invalidate(key),
    };
  }

  /** Invalidates a single cache entry by key, clearing data and allowing re-fetch. */
  invalidate(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry._data.set([]);
    entry._isLoading.set(false);
    entry._hasError.set(false);
    entry.obs$ = undefined;
    entry.loadedAt = undefined;
  }

  /** Invalidates all registered cache entries. */
  invalidateAll(): void {
    this.entries.forEach((_, key) => this.invalidate(key));
  }

  private _load(key: string): void {
    const entry = this.entries.get(key);
    const loaderFn = this.loaders.get(key);
    if (!entry || !loaderFn) return;

    const expired =
      entry.ttlMs !== undefined &&
      entry.loadedAt !== undefined &&
      Date.now() - entry.loadedAt > entry.ttlMs;

    if (entry._data().length > 0 && !expired) return;
    if (entry._isLoading()) return;

    entry._hasError.set(false);
    entry._isLoading.set(true);

    if (!entry.obs$ || expired) {
      entry.obs$ = loaderFn().pipe(shareReplay(1));
    }

    entry.obs$.subscribe({
      next: (data) => {
        entry._data.set(data);
        entry.loadedAt = Date.now();
        entry._isLoading.set(false);
      },
      error: () => {
        entry._isLoading.set(false);
        entry._hasError.set(true);
        entry.obs$ = undefined;
      },
    });
  }
}
